/**
 *    SPDX-License-Identifier: Apache-2.0
 */

import React, { Component } from 'react';
import { withStyles } from '@material-ui/core/styles';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import FontAwesome from 'react-fontawesome';
import { blockHashType } from '../types';

const styles = () => ({
	container: {
		minHeight: '100vh',
		backgroundColor: '#f5f5f5',
		padding: 0,
		margin: 0,
		marginBottom: 100,
		paddingBottom: 20,
		fontFamily: 'Arial, sans-serif'
	},
	header: {
		backgroundColor: '#4A90E2',
		color: '#ffffff',
		padding: '20px 40px',
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
	},
	headerLeft: {
		display: 'flex',
		alignItems: 'center',
		'& > *': {
			marginRight: '15px',
			'&:last-child': {
				marginRight: 0
			}
		}
	},
	logo: {
		fontSize: '24px',
		fontWeight: 'bold'
	},
	headerCenter: {
		display: 'flex',
		alignItems: 'center',
		'& > *': {
			marginRight: '10px',
			'&:last-child': {
				marginRight: 0
			}
		},
		fontSize: '20px',
		fontWeight: 'bold'
	},
	headerRight: {
		'& a': {
			color: '#ffffff',
			textDecoration: 'none',
			fontSize: '16px',
			'&:hover': {
				textDecoration: 'underline'
			}
		}
	},
	mainContent: {
		maxWidth: '1200px',
		margin: '40px auto',
		padding: '0 20px'
	},
	contentCard: {
		backgroundColor: '#ffffff',
		borderRadius: '8px',
		boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
		padding: '30px'
	},
	title: {
		fontSize: '24px',
		fontWeight: 'bold',
		marginBottom: '30px',
		color: '#333333',
		borderBottom: '2px solid #4A90E2',
		paddingBottom: '10px'
	},
	dataRow: {
		display: 'flex',
		padding: '15px 0',
		borderBottom: '1px solid #e0e0e0',
		'&:last-child': {
			borderBottom: 'none'
		}
	},
	label: {
		fontWeight: 'bold',
		width: '200px',
		color: '#666666',
		flexShrink: 0
	},
	value: {
		flex: 1,
		color: '#333333',
		wordBreak: 'break-word'
	},
	status: {
		color: '#28a745',
		fontWeight: 'bold'
	},
	blockLink: {
		color: '#4A90E2',
		textDecoration: 'none',
		'&:hover': {
			textDecoration: 'underline'
		}
	},
	copyButton: {
		background: 'none',
		border: 'none',
		cursor: 'pointer',
		marginLeft: '10px',
		color: '#666666',
		'&:hover': {
			color: '#4A90E2'
		}
	},
	loading: {
		textAlign: 'center',
		padding: '40px',
		fontSize: '18px',
		color: '#666666'
	},
	error: {
		textAlign: 'center',
		padding: '40px',
		fontSize: '18px',
		color: '#dc3545'
	},
	transactionList: {
		marginTop: '10px',
		'& ul': {
			listStyle: 'none',
			padding: 0,
			margin: 0,
			'& li': {
				marginBottom: '8px',
				'& a': {
					color: '#4A90E2',
					textDecoration: 'none',
					'&:hover': {
						textDecoration: 'underline'
					}
				}
			}
		}
	}
});

export class BlockDetailPage extends Component {
	componentDidMount() {
		const { match, blockSearch, currentChannel, blockHash, blockListSearch, getBlockSearch } = this.props;
		const blockHashParam = match && match.params && match.params.blockHash;
		
		// Try to find block by hash in blockListSearch first
		if (blockHashParam && blockListSearch) {
			const foundBlock = blockListSearch.find(b => b.blockhash === blockHashParam);
			
			if (foundBlock && getBlockSearch) {
				// Use found block data
				getBlockSearch({ data: foundBlock });
			} else if (blockSearch) {
				// Try to fetch by block number if hash not found
				blockSearch(currentChannel, blockHashParam);
			}
		}
	}

	componentDidUpdate(prevProps) {
		const { match, blockSearch, currentChannel, blockListSearch, getBlockSearch } = this.props;
		const blockHashParam = match && match.params && match.params.blockHash;
		const prevBlockHashParam = prevProps.match && prevProps.match.params && prevProps.match.params.blockHash;
		
		// Fetch block if blockHash changed
		if (blockHashParam && blockHashParam !== prevBlockHashParam) {
			if (blockListSearch) {
				const foundBlock = blockListSearch.find(b => b.blockhash === blockHashParam);
				
				if (foundBlock && getBlockSearch) {
					getBlockSearch({ data: foundBlock });
				} else if (blockSearch) {
					blockSearch(currentChannel, blockHashParam);
				}
			}
		}
	}

	formatTimestamp = timestamp => {
		if (!timestamp) return '-';
		const date = new Date(timestamp);
		return date.toLocaleString('en-US', {
			year: 'numeric',
			month: '2-digit',
			day: '2-digit',
			hour: '2-digit',
			minute: '2-digit',
			second: '2-digit',
			hour12: false
		}).replace(',', '');
	};

	handleBlockClick = (e, blockHash) => {
		e.preventDefault();
		const { history } = this.props;
		if (history && blockHash && blockHash !== '-') {
			history.push(`/blocks/${blockHash}`);
		}
	};

	handleTransactionClick = (e, txhash) => {
		e.preventDefault();
		if (txhash) {
			window.open(`#/transactions/${txhash}`, '_blank');
		}
	};

	render() {
		const { blockHash, classes, currentChannel } = this.props;

		if (!blockHash || !blockHash.blockhash) {
			return (
				<div className={classes.container}>
					<div className={classes.header}>
						<div className={classes.headerLeft}>
							<div className={classes.logo}>Hyperledger Explorer</div>
						</div>
						<div className={classes.headerCenter}>
							<span>Block Details</span>
						</div>
						<div className={classes.headerRight}>
							<a
								href="#"
								onClick={e => {
									e.preventDefault();
									const { history } = this.props;
									if (history) {
										history.push('/');
									}
								}}
							>
								HOME
							</a>
						</div>
					</div>
					<div className={classes.loading}>
						<FontAwesome name="circle-o-notch" size="2x" spin /> Loading block...
					</div>
				</div>
			);
		}

		if (blockHash.error) {
			return (
				<div className={classes.container}>
					<div className={classes.header}>
						<div className={classes.headerLeft}>
							<div className={classes.logo}>Hyperledger Explorer</div>
						</div>
						<div className={classes.headerCenter}>
							<span>Block Details</span>
						</div>
						<div className={classes.headerRight}>
							<a
								href="#"
								onClick={e => {
									e.preventDefault();
									const { history } = this.props;
									if (history) {
										history.push('/');
									}
								}}
							>
								HOME
							</a>
						</div>
					</div>
					<div className={classes.error}>
						Error loading block: {blockHash.error}
					</div>
				</div>
			);
		}

		return (
			<div className={classes.container}>
				<div className={classes.header}>
					<div className={classes.headerLeft}>
						<div className={classes.logo}>Hyperledger Explorer</div>
					</div>
					<div className={classes.headerCenter}>
						<span>Block Details</span>
					</div>
					<div className={classes.headerRight}>
						<a
							href="#"
							onClick={e => {
								e.preventDefault();
								const { history } = this.props;
								if (history) {
									history.push('/');
								}
							}}
						>
							HOME
						</a>
					</div>
				</div>

				<div className={classes.mainContent}>
					<div className={classes.contentCard}>
						<div className={classes.title}>Block data</div>

						<div className={classes.dataRow}>
							<div className={classes.label}>Block Number:</div>
							<div className={classes.value}>
								{blockHash.blocknum || '-'}
							</div>
						</div>

						<div className={classes.dataRow}>
							<div className={classes.label}>Channel:</div>
							<div className={classes.value}>
								{blockHash.channelname || currentChannel || '-'}
							</div>
						</div>

						<div className={classes.dataRow}>
							<div className={classes.label}>Created at:</div>
							<div className={classes.value}>
								{this.formatTimestamp(blockHash.createdt)}
							</div>
						</div>

						<div className={classes.dataRow}>
							<div className={classes.label}>Number of Transactions:</div>
							<div className={classes.value}>
								{blockHash.txcount || '0'}
							</div>
						</div>

						<div className={classes.dataRow}>
							<div className={classes.label}>Block Hash:</div>
							<div className={classes.value}>
								{blockHash.blockhash || '-'}
								{blockHash.blockhash && (
									<CopyToClipboard text={blockHash.blockhash}>
										<button className={classes.copyButton} type="button">
											<FontAwesome name="copy" />
										</button>
									</CopyToClipboard>
								)}
							</div>
						</div>

						<div className={classes.dataRow}>
							<div className={classes.label}>Data Hash:</div>
							<div className={classes.value}>
								{blockHash.datahash || '-'}
								{blockHash.datahash && (
									<CopyToClipboard text={blockHash.datahash}>
										<button className={classes.copyButton} type="button">
											<FontAwesome name="copy" />
										</button>
									</CopyToClipboard>
								)}
							</div>
						</div>

						<div className={classes.dataRow}>
							<div className={classes.label}>Previous Hash:</div>
							<div className={classes.value}>
								{blockHash.prehash ? (
									<>
										<a
											href={`/blocks/${blockHash.prehash}`}
											className={classes.blockLink}
											onClick={e => this.handleBlockClick(e, blockHash.prehash)}
										>
											{blockHash.prehash}
										</a>
										<CopyToClipboard text={blockHash.prehash}>
											<button className={classes.copyButton} type="button">
												<FontAwesome name="copy" />
											</button>
										</CopyToClipboard>
									</>
								) : (
									'-'
								)}
							</div>
						</div>

						{blockHash.txhash && blockHash.txhash.length > 0 && (
							<div className={classes.dataRow}>
								<div className={classes.label}>Transactions:</div>
								<div className={`${classes.value} ${classes.transactionList}`}>
									<ul>
										{blockHash.txhash.map((tid, index) => (
											<li key={index}>
												<a
													href={`#/transactions/${tid}`}
													onClick={e => this.handleTransactionClick(e, tid)}
													target="_blank"
													rel="noopener noreferrer"
												>
													{tid}
												</a>
											</li>
										))}
									</ul>
								</div>
							</div>
						)}

						{blockHash.blksize && (
							<div className={classes.dataRow}>
								<div className={classes.label}>Size (KB):</div>
								<div className={classes.value}>
									{blockHash.blksize || '-'}
								</div>
							</div>
						)}
					</div>
				</div>
			</div>
		);
	}
}

BlockDetailPage.propTypes = {
	blockHash: blockHashType,
	classes: (props, propName) => {
		if (typeof props[propName] !== 'object') {
			return new Error(`${propName} must be an object`);
		}
	},
	history: (props, propName) => {
		if (props[propName] && typeof props[propName].push !== 'function') {
			return new Error(`${propName} must be a history object`);
		}
	},
	currentChannel: (props, propName) => {
		if (props[propName] && typeof props[propName] !== 'string') {
			return new Error(`${propName} must be a string`);
		}
	},
	match: (props, propName) => {
		if (props[propName] && typeof props[propName] !== 'object') {
			return new Error(`${propName} must be an object`);
		}
	},
	blockSearch: (props, propName) => {
		if (props[propName] && typeof props[propName] !== 'function') {
			return new Error(`${propName} must be a function`);
		}
	},
	blockListSearch: (props, propName) => {
		if (props[propName] && !Array.isArray(props[propName])) {
			return new Error(`${propName} must be an array`);
		}
	},
	getBlockSearch: (props, propName) => {
		if (props[propName] && typeof props[propName] !== 'function') {
			return new Error(`${propName} must be a function`);
		}
	}
};

BlockDetailPage.defaultProps = {
	blockHash: null,
	history: null,
	currentChannel: '',
	match: null,
	blockSearch: null,
	blockListSearch: [],
	getBlockSearch: null
};

export default withStyles(styles)(BlockDetailPage);

